import { Injectable, ErrorHandler, Injector } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { AuthService } from './auth.service';

export interface ErrorContext {
  component?: string;
  method?: string;
  userAction?: string;
  timestamp: Date;
  userId?: string | null;
  additionalData?: any;
}

export interface ErrorRecoveryAction {
  action: 'retry' | 'reconnect' | 'reload' | 'logout' | 'navigate';
  data?: any;
}

@Injectable({
  providedIn: 'root'
})
export class ErrorBoundaryService implements ErrorHandler {
  private errorCount = 0;
  private readonly MAX_ERRORS = 5;
  private readonly ERROR_RESET_INTERVAL = 60000; // 1 minute

  constructor(
    private injector: Injector,
    private snackBar: MatSnackBar,
    private router: Router,
    private authService: AuthService
  ) {
    // Reset error count periodically
    setInterval(() => {
      this.errorCount = 0;
    }, this.ERROR_RESET_INTERVAL);
  }

  handleError(error: Error): void {
    this.errorCount++;
    
    const errorContext: ErrorContext = {
      component: this.getComponentName(error),
      method: this.getMethodName(error),
      timestamp: new Date(),
      userId: this.authService.getUserId(),
      additionalData: {
        stack: error.stack,
        message: error.message,
        errorCount: this.errorCount
      }
    };

    console.error('Error caught by ErrorBoundaryService:', error, errorContext);

    // Log to monitoring service (if available)
    this.logErrorToMonitoring(error, errorContext);

    // Handle critical errors
    if (this.isCriticalError(error)) {
      this.handleCriticalError(error, errorContext);
      return;
    }

    // Handle rate limiting
    if (this.isRateLimitError(error)) {
      this.handleRateLimitError(error);
      return;
    }

    // Handle network errors
    if (this.isNetworkError(error)) {
      this.handleNetworkError(error);
      return;
    }

    // Handle authentication errors
    if (this.isAuthError(error)) {
      this.handleAuthError(error);
      return;
    }

    // Handle chat-specific errors
    if (this.isChatError(error)) {
      this.handleChatError(error);
      return;
    }

    // Default error handling
    this.handleGenericError(error, errorContext);
  }

  private isCriticalError(error: Error): boolean {
    return this.errorCount > this.MAX_ERRORS || 
           error.message.includes('OutOfMemory') ||
           error.message.includes('Maximum call stack size exceeded');
  }

  private isRateLimitError(error: Error): boolean {
    return error.message.includes('rate limit') || 
           error.message.includes('too many requests') ||
           error.message.includes('RATE_LIMIT');
  }

  private isNetworkError(error: Error): boolean {
    return error.message.includes('NetworkError') ||
           error.message.includes('Failed to fetch') ||
           error.message.includes('ERR_NETWORK') ||
           error.message.includes('ERR_INTERNET_DISCONNECTED');
  }

  private isAuthError(error: Error): boolean {
    return error.message.includes('Unauthorized') ||
           error.message.includes('Forbidden') ||
           error.message.includes('Token expired') ||
           error.message.includes('Invalid token');
  }

  private isChatError(error: Error): boolean {
    return error.message.includes('chat') ||
           error.message.includes('message') ||
           error.message.includes('socket') ||
           error.message.includes('connection');
  }

  private handleCriticalError(error: Error, context: ErrorContext): void {
    console.error('Critical error detected:', error);
    
    this.snackBar.open(
      'A critical error occurred. The application will reload to prevent further issues.',
      'Reloading...',
      { duration: 5000 }
    );

    // Force reload after a delay
    setTimeout(() => {
      window.location.reload();
    }, 3000);
  }

  private handleRateLimitError(error: Error): void {
    this.snackBar.open(
      'Too many requests. Please wait a moment before trying again.',
      'OK',
      { duration: 3000 }
    );
  }

  private handleNetworkError(error: Error): void {
    this.snackBar.open(
      'Network connection lost. Please check your internet connection.',
      'Retry',
      { duration: 5000 }
    ).onAction().subscribe(() => {
      // Attempt to reconnect
      this.attemptReconnection();
    });
  }

  private handleAuthError(error: Error): void {
    this.snackBar.open(
      'Authentication error. Please log in again.',
      'Login',
      { duration: 5000 }
    ).onAction().subscribe(() => {
      this.authService.logout();
      this.router.navigate(['/login']);
    });
  }

  private handleChatError(error: Error): void {
    this.snackBar.open(
      'Chat connection issue. Attempting to reconnect...',
      'OK',
      { duration: 3000 }
    );

    // Attempt to reconnect to chat
    this.attemptChatReconnection();
  }

  private handleGenericError(error: Error, context: ErrorContext): void {
    const userMessage = this.getUserFriendlyMessage(error);
    
    this.snackBar.open(
      userMessage,
      'Dismiss',
      { duration: 4000 }
    );
  }

  private getUserFriendlyMessage(error: Error): string {
    const errorMessage = error.message.toLowerCase();
    
    if (errorMessage.includes('file')) {
      return 'There was an issue with the file. Please try again.';
    }
    
    if (errorMessage.includes('upload')) {
      return 'File upload failed. Please check your connection and try again.';
    }
    
    if (errorMessage.includes('message')) {
      return 'Failed to send message. Please try again.';
    }
    
    if (errorMessage.includes('connection')) {
      return 'Connection issue detected. Please check your internet.';
    }
    
    return 'Something went wrong. Please try again.';
  }

  private getComponentName(error: Error): string {
    const stack = error.stack || '';
    const componentMatch = stack.match(/at\s+(\w+Component)/);
    return componentMatch ? componentMatch[1] || 'Unknown' : 'Unknown';
  }

  private getMethodName(error: Error): string {
    const stack = error.stack || '';
    const methodMatch = stack.match(/at\s+\w+\.(\w+)/);
    return methodMatch ? methodMatch[1] || 'Unknown' : 'Unknown';
  }

  private logErrorToMonitoring(error: Error, context: ErrorContext): void {
    // In a real application, you would send this to a monitoring service
    // like Sentry, LogRocket, or your own error tracking system
    const errorData = {
      message: error.message,
      stack: error.stack,
      context,
      userAgent: navigator.userAgent,
      url: window.location.href,
      timestamp: new Date().toISOString()
    };

    // For now, just log to console
    console.group('Error Report');
    console.log('Error Data:', errorData);
    console.groupEnd();

    // You could also send to your backend
    // this.http.post('/api/errors', errorData).subscribe();
  }

  private attemptReconnection(): void {
    // Attempt to reconnect to the application
    console.log('Attempting to reconnect...');
    
    // You could implement a reconnection strategy here
    // For example, retry failed requests, reconnect WebSocket, etc.
  }

  private attemptChatReconnection(): void {
    // Attempt to reconnect to chat service
    console.log('Attempting to reconnect to chat...');
    
    // You could implement chat-specific reconnection here
    // For example, reconnect WebSocket, retry failed messages, etc.
  }

  // Public methods for components to use
  public handleComponentError(error: Error, context: Partial<ErrorContext> = {}): void {
    const fullContext: ErrorContext = {
      ...context,
      timestamp: new Date(),
      userId: this.authService.getUserId()
    };

    this.handleError(error);
  }

  public showUserFriendlyError(message: string, action?: string): void {
    this.snackBar.open(message, action || 'OK', { duration: 4000 });
  }

  public getRecoveryAction(error: Error): ErrorRecoveryAction | null {
    if (this.isNetworkError(error)) {
      return { action: 'reconnect' };
    }
    
    if (this.isAuthError(error)) {
      return { action: 'logout' };
    }
    
    if (this.isRateLimitError(error)) {
      return { action: 'retry', data: { delay: 5000 } };
    }
    
    return null;
  }
} 